import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useMsal } from '@azure/msal-react';
import { Divider } from 'uiKit/atoms/Divider/Divider';
import { Checkbox } from 'uiKit/atoms/Checkbox/Checkbox';
import { Button } from 'uiKit/atoms/Button/Button';

import { useToggle } from 'hooks';
import { displayMessage } from 'helpers/utils';
import { TermsAgreement } from './Documents/TermsAgreement';
import { TermsDocuments } from './Documents/TermsDocuments';
import { preventBodyScroll } from '../../utils';
import { ReactComponent as BNSLogoSvgBig } from 'assets/img/bns-logo-big.svg';
import ContactUs from 'components/Footer/components/ContactUs';
import { useStores } from 'stores';

import clsx from 'clsx';
import styles from './styles.module.scss';
import { Paragraph } from 'uiKit/atoms/Typography/components/Paragraph';

const PreLoginPage = () => {
  const {
    authStore: { azureADLoginHandler },
  } = useStores();

  const [isAgreeOne, toggleIsAgreeOne] = useToggle(false);
  const [isAgreeTwo, toggleIsAgreeTwo] = useToggle(false);
  const [isShowOne, toggleIsShowOne] = useToggle(false);
  const [isShowTwo, toggleIsShowTwo] = useToggle(false);
  const [isErrorMessage, toggleIsErrorMessage] = useToggle(false);

  const { inProgress } = useMsal();

  // Reset scroll lock when both modals closed
  useEffect(() => {
    if (!isShowOne && !isShowTwo) {
      preventBodyScroll.reset();
    }
  }, [isShowOne, isShowTwo]);

  const blockParagraphStyles = clsx(styles.bns3_pre_login__terms_block_paragraph, {
    [styles.bns3_pre_login__error_color]: isErrorMessage,
  });

  const agreeHandlerOne = () => {
    toggleIsAgreeOne(true);
    toggleIsShowOne(false);
  };

  const agreeHandlerTwo = () => {
    toggleIsAgreeTwo(true);
    toggleIsShowTwo(false);
  };

  const termsBtnClickHandler = () => {
    toggleIsShowOne(true);
    preventBodyScroll('add');
  };

  const privacyBtnClickHandler = () => {
    toggleIsShowTwo(true);
    preventBodyScroll('add');
  };

  const handleLogin = () => {
    if (!(isAgreeOne && isAgreeTwo)) {
      return toggleIsErrorMessage(true);
    }

    if (inProgress !== 'login') {
      return azureADLoginHandler();
    }

    displayMessage('Login is currently in progress! Use already opened popup.');
  };

  return (
    <div>
      <div className={styles.bns3_pre_login}>
        <div className={styles.bns3_pre_login__header} />
        <div className={styles.bns3_pre_login_mainContainer}>
          <div className={styles.bns3_pre_login__main}>
            <div className={styles.bns3_pre_login__logo}>
              <BNSLogoSvgBig />
            </div>

            <div className={styles.bns3_pre_login__description}>
              You can use this service only if you are an employee of DataArt or have explicit permission to do so.
              Otherwise, please leave this service immediately.
            </div>

            <div>
              <Button
                size="large"
                onClick={handleLogin}
                disabled={!isAgreeOne || !isAgreeTwo}
                className={styles.bns3_pre_login__login_button}
                data-testid="button"
              >
                Login
              </Button>
            </div>

            <Paragraph className={blockParagraphStyles}>
              Before login you have to read and agree with the two documents below.
            </Paragraph>

            <div className={styles.bns3_pre_login__terms_block}>
              <div>
                <Checkbox id="login-terms-conditions" checked={isAgreeOne} onChange={toggleIsAgreeOne} size="small" />
                <Button
                  sx={{
                    all: 'unset',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    cursor: 'pointer',
                  }}
                  variant="text"
                  onClick={termsBtnClickHandler}
                >
                  Terms and Conditions
                </Button>
              </div>

              <Divider orientation="vertical" flexItem />

              <div>
                <Checkbox id="login-privacy-policy" checked={isAgreeTwo} onChange={toggleIsAgreeTwo} size="small" />
                <Button
                  sx={{
                    all: 'unset',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    cursor: 'pointer',
                  }}
                  variant="text"
                  onClick={privacyBtnClickHandler}
                >
                  Privacy Policy
                </Button>
              </div>
            </div>

            <TermsAgreement
              open={isShowOne}
              closeHandlerOne={() => toggleIsShowOne(false)}
              agreeHandlerOne={agreeHandlerOne}
            />

            <TermsDocuments
              open={isShowTwo}
              closeHandlerTwo={() => toggleIsShowTwo(false)}
              agreeHandlerTwo={agreeHandlerTwo}
            />
          </div>
        </div>
      </div>

      <ContactUs />
    </div>
  );
};

export default observer(PreLoginPage);
